// ✅ Lucide icons
import {
  HelpCircle,
  Lock,
  LogOut,
  Moon,
  Settings,
  Sun,
  User,
  Wallet,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { PATHS } from '@/constants/page-paths';
import { useGetProfile } from '@/lib/hooks/api/profile.hook';
import { usePermission } from '@/lib/hooks/useRoutePermission';
import {
  useExternalCurrencyStore,
  useSettingsStore,
  useUserStore,
} from '@/store';
import { useAppConfig } from '../layouts/AppConfigProvider';
import { Logout } from '../shared/Logout';
import { Web3Wallet } from '../shared/web3-wallet/web3-wallet';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';

interface ProfileMenuList {
  title: string;
  path: string;
  icon: React.ElementType;
}

const profileMenus: ProfileMenuList[] = [
  { title: 'Profile', path: `${PATHS.PROFILE}?tab=account`, icon: User },
  { title: 'Settings', path: `${PATHS.PROFILE}?tab=settings`, icon: Settings },
  { title: 'Vault', path: `${PATHS.PROFILE}?tab=vault`, icon: Lock },
];

export function ProfileDropdown() {
  const { user } = useUserStore();
  const { data } = useGetProfile();
  const { settings } = useSettingsStore();
  const { externalCurrencyEnabled } = useExternalCurrencyStore();

  const tokenEnabled = settings?.app?.isTokenEnabled ?? false;
  const userPays = settings?.suVariables?.userPays;
  const { colorScheme } = useAppConfig();

  const profile = data?.data?.data ?? user;
  const { setThemeMode, themeMode } = useSettingsStore();

  const toggleTheme = () => {
    setThemeMode(themeMode === 'light' ? 'dark' : 'light');
  };

  const filteredProfileMenus = profileMenus.filter((menu) =>
    menu.title === 'Vault' ? userPays : true
  );

  const checkPermission = usePermission();
  const checkHelpFeedback = checkPermission('help_and_feedback');

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <div
          role="button"
          className="flex items-center h-full rounded-lg gap-3 hover:bg-secondary/70 transition px-2 py-1 cursor-pointer"
          data-testid="profile-dropdown-trigger"
        >
          {profile?.firstName && (
            <h5 className="capitalize hidden md:block font-medium text-sm">
              {profile?.firstName}
            </h5>
          )}

          <Avatar className="size-9 border border-border shadow-sm">
            <AvatarImage
              src={profile?.userImage as string}
              alt="profile"
              className="object-cover"
            />
            <AvatarFallback className="text-base font-semibold uppercase">
              {profile?.name?.[0] ?? '?'}
            </AvatarFallback>
          </Avatar>
        </div>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        className="min-w-[220px] rounded-xl p-1 shadow-lg"
        align="end"
      >
        <DropdownMenuLabel className="pb-1">
          <p className="font-semibold">{profile?.name}</p>
          <p className="text-muted-foreground text-sm">{profile?.email}</p>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        {/* Web3 Wallet */}
        {tokenEnabled && !externalCurrencyEnabled && (
          <>
            <DropdownMenuItem className="sm:hidden flex items-center gap-2">
              <Wallet className="h-4 w-4 text-muted-foreground" />
              <Web3Wallet />
            </DropdownMenuItem>
            <DropdownMenuSeparator className="sm:hidden" />
          </>
        )}

        {/* Profile Menus */}
        <DropdownMenuGroup>
          {filteredProfileMenus
            .filter(
              (menu) => !(menu.title === 'Vault' && externalCurrencyEnabled)
            )
            .map(({ title, path, icon: Icon }, i) => (
              <Link to={path} key={i}>
                <DropdownMenuItem className="w-full flex items-center gap-2 cursor-pointer rounded-md hover:bg-accent transition">
                  <Icon className="h-4 w-4 text-muted-foreground" />
                  <span>{title}</span>
                </DropdownMenuItem>
              </Link>
            ))}
        </DropdownMenuGroup>

        {/* Theme Mode Toggle */}
        {colorScheme === 'auto' && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="flex items-center gap-2 cursor-pointer rounded-md hover:bg-accent transition"
              onClick={toggleTheme}
              data-testid="mode-switcher"
            >
              {themeMode === 'dark' ? (
                <>
                  <Sun className="h-4 w-4 text-muted-foreground" />
                  <span>Light Mode</span>
                </>
              ) : (
                <>
                  <Moon className="h-4 w-4 text-muted-foreground" />
                  <span>Dark Mode</span>
                </>
              )}
            </DropdownMenuItem>
          </>
        )}

        {/* Help & Feedback */}
        {checkHelpFeedback && (
          <>
            <DropdownMenuSeparator />
            <Link to={PATHS.FEEDBACK}>
              <DropdownMenuItem className="w-full flex items-center gap-2 cursor-pointer rounded-md hover:bg-accent transition">
                <HelpCircle className="h-4 w-4 text-muted-foreground" />
                <span>Help & Feedback</span>
              </DropdownMenuItem>
            </Link>
          </>
        )}

        {/* Logout */}
        <DropdownMenuSeparator />
        <Logout>
          <DropdownMenuItem
            className="w-full flex items-center gap-2 cursor-pointer rounded-md hover:bg-accent transition"
            data-testid="logout-profile-dropdown"
            onSelect={(e) => {
              e.preventDefault(); 
            }}
          >
            <div className='w-full flex items-center gap-2'>
              <LogOut className="h-4 w-4 text-muted-foreground" />
              <span>Log out</span>
            </div>
          </DropdownMenuItem>
        </Logout>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
